<template>
  <svg
    aria-hidden="true"
    focusable="false"
    :viewBox="VIEW_BOX[props.variant ?? 'flat']"
  >
    <!-- Flat inherits currentColor, so the caller tints it. The other two carry
         the real artwork's own fills and cannot be tinted at all. -->
    <use
      v-if="isFlat"
      href="#pj-mark-flat"
    />

    <!-- Both grounds are rendered and CSS picks one. href cannot be switched by
         a stylesheet, and resolving the theme in JS would mean the wrong mark on
         the first paint of every page — the ground is already decided in CSS
         before Vue has run. -->
    <template v-else>
      <use
        class="pj-on-light"
        :href="`#${symbol}`"
      />
      <use
        class="pj-on-dark"
        :href="`#${symbol}-dark`"
      />
    </template>
  </svg>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

/**
 * The Pjazza mark, and the mark-plus-wordmark lockup.
 *
 * Three variants, because the mark does two different jobs:
 *
 * - `flat` — one colour, inherits currentColor. For anywhere the shape means
 *   something other than "Pjazza": a ghosted empty state, the ready screen. Brand
 *   artwork must not recolour to signal state, so those uses get the monochrome
 *   lockup instead of the artwork.
 * - `mark` / `logo` — the real thing, exported from Figma and not redrawn. The
 *   woven look is five per-bar gradients; the light ground is navy on
 *   #121835 shading and the dark ground is a pale pink through an eight-stop
 *   ramp. They are not a recolour of each other, which is why both ship.
 *
 * Sizing is the caller's: pass height and width classes as you would to any svg.
 */
const props = defineProps<{ variant?: 'flat' | 'mark' | 'logo' }>()

const VIEW_BOX = {
  flat: '0 0 67 67',
  mark: '0 0 67 67',
  logo: '0 0 152 43'
} as const

const isFlat = computed(() => (props.variant ?? 'flat') === 'flat')

const symbol = computed(() => (props.variant === 'logo' ? 'pj-logo' : 'pj-mark'))
</script>
